import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../constants/Colors";
import {Checkbox, Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import {SearchRounded} from "@material-ui/icons";
import {Button} from "@zendeskgarden/react-buttons";
import {
    FetchGprEntityGroupsQuery,
    PropertyType,
    useFetchGprEntityGroupsLazyQuery
} from "../../../__generated__/generated_types";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, Table} from "@zendeskgarden/react-tables";
import SelectionBar from "../users/components/SelectionBar";
import {GPREGroupFormModal} from "./GPREGroupFormModal";
import {GPREGroupDeleteModal} from "./GPREGroupDeleteModal";
import {GPREGroupAccountsView} from "./GPREGroupAccountsView";
import {GPREGroupPropertiesView} from "./GPREGroupPropertiesView";
import * as css from "./styles/gprEntityStyles.module.scss";
import {AdminWrapper} from "../AdminWrapper";
import AdminHeader from "../AdminHeader";
import {useProperties} from "../../../contexts/properties/PropertiesContext";
import {useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {AuthContext} from "../../../contexts/AuthContext";
import {
    groupKey,
    isDefaultGroup,
    previewNames
} from "./logic";
import {formatterGlName} from "../../../utils/formatters";

type GprGroup = FetchGprEntityGroupsQuery["fetchGPREGroups"][0];

type ActiveView =
    | {kind: "groups"}
    | {kind: "accounts"; group: GprGroup}
    | {kind: "properties"; group: GprGroup};

// We may decide to allow these properties to have special GPR Setup. But not sure we need it yet.
const DISALLOWED_PROPERTY_TYPES = [PropertyType.Acquisition, PropertyType.Template];

export function GPREGroupList(): ReactElement {
    const {budgetYear} = useContext(AuthContext);
    const [fetchGroups, {data, loading}] = useFetchGprEntityGroupsLazyQuery({
        fetchPolicy: "no-cache"
    });
    const {properties} = useProperties();
    const {chartOfAccountsFlat} = useChartOfAccounts();

    const [search, setSearch] = useState<string | null>(null);
    const [deleteVisible, setDeleteVisible] = useState<boolean>(false);
    const [createFormVisible, setCreateFormVisible] = useState<boolean>(false);
    const [editingGroup, setEditingGroup] = useState<GprGroup | null>(null);
    const [filteredData, setFilteredData] = useState<GprGroup[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [activeView, setActiveView] = useState<ActiveView>({kind: "groups"});

    const [allowedProperties, propertyNameMap] = useMemo(() => {
        if(!properties) {
            return [[], new Map<string, string>()];
        }
        const filteredProperties = properties.filter(p => !DISALLOWED_PROPERTY_TYPES.includes(p.propertyType));
        const map = new Map<string, string>();
        for(const p of filteredProperties) {
            map.set(p.id, p.name);
        }
        return [filteredProperties, map];
    }, [properties]);

    const coaNameMap = useMemo(() => {
        const map = new Map<string, string>();
        if(chartOfAccountsFlat) {
            for(const entity of chartOfAccountsFlat) {
                map.set(entity.id, formatterGlName(entity.name, entity.number));
            }
        }
        return map;
    }, [chartOfAccountsFlat]);

    useEffect(() => {
        if(!budgetYear) {
            return;
        }
        fetchGroups({variables: {budgetYear}});
    }, [budgetYear]);

    useEffect(() => {
        if(loading) {
            return;
        }

        if(!data) {
            setFilteredData([]);
            return;
        }

        let filtered = data.fetchGPREGroups;
        if(search !== null && search !== "") {
            filtered = filtered.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));
        }
        setFilteredData(filtered);
    }, [data, loading, search]);

    function reloadGroups(): void {
        if(budgetYear) {
            fetchGroups({variables: {budgetYear}});
        }
    }

    function clearSelected() {
        setSelectedGroups([]);
    }

    function closeForm(saved: boolean): void {
        setCreateFormVisible(false);
        setEditingGroup(null);
        if(saved) {
            reloadGroups();
        }
    }

    function closeManageView(changed: boolean): void {
        setActiveView({kind: "groups"});
        if(changed) {
            reloadGroups();
        }
    }

    const showGroupList = activeView.kind === "groups";

    return <AdminWrapper>
        <AdminHeader title="Initial GPR Setup" subtitle="Manage initial GPR setup for your company." />
        <div className={css.gprPageInner}>
            {showGroupList &&
                <>
                    <div className={css.tableHeaderActions}>
                        <h5 className={css.tableHeaderActionsLeft}>
                            Groups
                            {loading && <Inline size={24} color={COLORS.PRIMARY_500} aria-label="loading"/>}
                        </h5>
                        <div className={css.tableHeaderActionsRight}>
                            <Field>
                                <MediaInput
                                    className={css.tableHeaderActionsSearch}
                                    placeholder="Search"
                                    isCompact
                                    onChange={evt => setSearch(evt.target.value.toLowerCase())}
                                    start={
                                        <SearchRounded/>
                                    }
                                />
                            </Field>
                            <Button isPrimary className={css.tableHeaderActionsButton}
                                    onClick={() => setCreateFormVisible(true)}>
                                Add Group
                            </Button>
                        </div>
                    </div>
                    <div className={css.tableWrapper}>
                        <Table className={css.gprTable} size={"large"}>
                            <Head isSticky>
                                <HeaderRow className={css.headerRow}>
                                    <HeaderCell isMinimum className={css.checkboxCell}/>
                                    <HeaderCell style={{borderLeft: "none"}}>Name</HeaderCell>
                                    <HeaderCell>Accounts</HeaderCell>
                                    <HeaderCell>Properties</HeaderCell>
                                </HeaderRow>
                            </Head>
                            <Body>
                                {
                                    filteredData.map(group => {
                                        const accountNames = previewNames(group.gprEntities.map(entity => coaNameMap.get(entity.id) ?? entity.id));
                                        const propertyNames = previewNames(group.properties.map(p => propertyNameMap.get(p.id) ?? p.id));
                                        const defaultGroup = isDefaultGroup(group.id);
                                        return <Row key={groupKey(group.id)} className={css.bodyRow}>
                                            <Cell className={css.checkboxCell}>
                                                {!defaultGroup && group.id &&
                                                    <Field>
                                                        <Checkbox
                                                            className={css.checkboxInput}
                                                            checked={selectedGroups.includes(group.id)}
                                                            onChange={() => {
                                                                const id = group.id;
                                                                if(!id) {
                                                                    return;
                                                                }
                                                                const idx = selectedGroups.indexOf(id);
                                                                const copy = [...selectedGroups];
                                                                if(idx > -1) {
                                                                    copy.splice(idx, 1);
                                                                } else {
                                                                    copy.push(id);
                                                                }
                                                                setSelectedGroups(copy);
                                                            }}
                                                        >
                                                            <Label className={css.checkboxInput}/>
                                                        </Checkbox>
                                                    </Field>
                                                }
                                            </Cell>
                                            <Cell style={{borderLeft: "none"}}>{group.name}</Cell>
                                            <Cell className={css.previewCell}>
                                                <span
                                                    className={css.previewLink}
                                                    onClick={() => setActiveView({kind: "accounts", group})}
                                                >
                                                    {accountNames || "Manage Accounts"}
                                                </span>
                                            </Cell>
                                            <Cell className={css.previewCell}>
                                                {defaultGroup
                                                    ? <span className={css.staticLabel}>---</span>
                                                    : <span
                                                        className={css.previewLink}
                                                        onClick={() => setActiveView({kind: "properties", group})}
                                                    >
                                                        {propertyNames || "Manage Properties"}
                                                    </span>
                                                }
                                            </Cell>
                                        </Row>;
                                    })
                                }
                            </Body>
                        </Table>
                    </div>
                </>
            }
            {activeView.kind === "accounts" &&
                <GPREGroupAccountsView
                    group={activeView.group}
                    budgetYear={budgetYear}
                    closed={closeManageView}
                />
            }
            {activeView.kind === "properties" && activeView.group.id &&
                <GPREGroupPropertiesView
                    group={{
                        id: activeView.group.id,
                        name: activeView.group.name,
                        properties: activeView.group.properties
                    }}
                    allProperties={allowedProperties.map(p => ({id: p.id, name: p.name}))}
                    allGroups={data?.fetchGPREGroups ?? []}
                    budgetYear={budgetYear}
                    closed={closeManageView}
                />
            }
        </div>
        {showGroupList &&
            <div className={css.modalsWrapper}>
                <SelectionBar
                    className={css.selectionBar}
                    selected={selectedGroups}
                    onEdit={() => {
                        if(selectedGroups.length !== 1) {
                            return;
                        }
                        const id = selectedGroups[0];
                        const toEdit = data?.fetchGPREGroups.find(g => g.id === id);
                        if(!toEdit || isDefaultGroup(toEdit.id)) {
                            setEditingGroup(null);
                        } else {
                            setEditingGroup(toEdit);
                        }
                    }}
                    onDelete={() => setDeleteVisible(true)}
                    name="Group"
                    namePlural="Groups"
                />
                {createFormVisible &&
                    <GPREGroupFormModal closed={closeForm} budgetYear={budgetYear} existingGroup={undefined}/>
                }
                {editingGroup !== null && editingGroup.id &&
                    <GPREGroupFormModal
                        closed={closeForm}
                        budgetYear={budgetYear}
                        existingGroup={{id: editingGroup.id, name: editingGroup.name}}
                    />
                }
                {deleteVisible &&
                    <GPREGroupDeleteModal
                        close={deleted => {
                            setDeleteVisible(false);
                            if(deleted) {
                                clearSelected();
                                reloadGroups();
                            }
                        }}
                        budgetYear={budgetYear}
                        groupIds={selectedGroups}
                    />
                }
            </div>
        }
    </AdminWrapper>;
}
